import { HTTP_API_V2 } from '../../http-common'

// Logical capability flag of the Inobram Platform feature and the attributes
// that gate its plan-dependent features (alarms, correlation charts, batch
// insights, comfort metrics and bird weight). A device only exposes one of them
// when its active subscription plan carries the Platform feature with the
// matching attribute turned on (premium plans; formerly the experimental plan,
// ended in IND-2116).
const PLATFORM_FEATURE_FLAG = 'platform'
const ALARMS_ATTRIBUTE_KEY = 'alarms'
const CORRELATION_CHARTS_ATTRIBUTE_KEY = 'correlation_charts'
const BATCH_INSIGHTS_ATTRIBUTE_KEY = 'batch_insights'
const COMFORT_METRICS_ATTRIBUTE_KEY = 'comfort_metrics'
const BIRD_WEIGHT_ATTRIBUTE_KEY = 'bird_weight'

function isAttributeEnabled (value) {
  return String(value).trim().toLowerCase() === 'true'
}

function deviceHasAttributeEnabled (deviceFeatures, attributeKey) {
  const features = (deviceFeatures && deviceFeatures.features) || []
  return features.some(feature => {
    if (feature.feature_flag !== PLATFORM_FEATURE_FLAG) {
      return false
    }
    const attributes = feature.attributes || []
    return attributes.some(attribute =>
      attribute.key === attributeKey && isAttributeEnabled(attribute.value)
    )
  })
}

function enabledDeviceIds (state, attributeKey) {
  return state.loadedDeviceFeaturesV2
    .filter(deviceFeatures => deviceHasAttributeEnabled(deviceFeatures, attributeKey))
    .map(deviceFeatures => deviceFeatures.deviceId)
}

export default {
  state: {
    loadedDeviceFeaturesV2: [],
    // Distinguishes "not loaded yet" from "loaded, no subscribed devices", which
    // an empty list cannot. Decisions that must not flip once data arrives —
    // such as swapping the shell for an AEI-only customer — wait for it.
    deviceFeaturesLoaded: false
  },
  getters: {
    loadedDeviceFeaturesV2 (state) {
      return state.loadedDeviceFeaturesV2
    },
    deviceFeaturesLoaded (state) {
      return state.deviceFeaturesLoaded
    },
    // Device ids whose active plan enables the alarms feature.
    alarmEnabledDeviceIds (state) {
      return enabledDeviceIds(state, ALARMS_ATTRIBUTE_KEY)
    },
    // True when the current user has at least one device with alarms enabled.
    hasAlarmsFeature (state, getters) {
      return getters.alarmEnabledDeviceIds.length > 0
    },
    // Device ids whose active plan enables the correlation charts.
    correlationChartsEnabledDeviceIds (state) {
      return enabledDeviceIds(state, CORRELATION_CHARTS_ATTRIBUTE_KEY)
    },
    // True when the current user has at least one device with correlation charts enabled.
    hasCorrelationChartsFeature (state, getters) {
      return getters.correlationChartsEnabledDeviceIds.length > 0
    },
    // Device ids whose active plan enables the batch insights.
    batchInsightsEnabledDeviceIds (state) {
      return enabledDeviceIds(state, BATCH_INSIGHTS_ATTRIBUTE_KEY)
    },
    // True when the current user has at least one device with batch insights enabled.
    hasBatchInsightsFeature (state, getters) {
      return getters.batchInsightsEnabledDeviceIds.length > 0
    },
    // Device ids whose active plan enables the comfort metrics
    // (card metric/comfort-index customization).
    comfortMetricsEnabledDeviceIds (state) {
      return enabledDeviceIds(state, COMFORT_METRICS_ATTRIBUTE_KEY)
    },
    // True when the current user has at least one device with comfort metrics enabled.
    hasComfortMetricsFeature (state, getters) {
      return getters.comfortMetricsEnabledDeviceIds.length > 0
    },
    // Device ids whose active plan enables the bird weight screen
    // (weighing history + weight analysis and projection).
    birdWeightEnabledDeviceIds (state) {
      return enabledDeviceIds(state, BIRD_WEIGHT_ATTRIBUTE_KEY)
    },
    // True when the current user has at least one device with bird weight enabled.
    hasBirdWeightFeature (state, getters) {
      return getters.birdWeightEnabledDeviceIds.length > 0
    }
  },
  mutations: {
    setLoadedDeviceFeaturesV2 (state, payload) {
      state.loadedDeviceFeaturesV2 = payload
      state.deviceFeaturesLoaded = true
    },
    clearLoadedDeviceFeaturesV2 (state) {
      state.loadedDeviceFeaturesV2 = []
      state.deviceFeaturesLoaded = false
    }
  },
  actions: {
    loadDeviceFeaturesV2 ({ commit }) {
      return HTTP_API_V2.get('devices/features')
        .then(result => {
          const deviceFeatures = Array.isArray(result.data) ? result.data : []
          commit('setLoadedDeviceFeaturesV2', deviceFeatures)
          return deviceFeatures
        })
        .catch(err => {
          // A failure here must not break the app; it simply leaves the user
          // without the plan-dependent features (alarms hidden).
          console.log(err)
          // Left unloaded on purpose: an unknown feature list must not be read
          // as "no Inobram devices" and swap a mixed customer's shell.
          commit('clearLoadedDeviceFeaturesV2', [])
          return []
        })
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/device_features/index.js